import React, { useState, useEffect } from 'react'
import { Button, ButtonGroup, Tooltip, OverlayTrigger } from 'react-bootstrap'
import BulkMoveModal from './BulkMoveModal'
import BulkIssueModal from './BulkIssueModal'
import BulkDisposeModal from './BulkDisposeModal'
import { usePermissions } from '../contexts/PermissionContext'

function BulkOperationsToolbar({
    selectedItems = [],
    inventoryItems = [],
    warehouseMap = {},
    onSelectionClear,
    onRefreshData,
    className = ""
}) {
    const { hasPermission } = usePermissions()

    // Modal states
    const [showBulkMoveModal, setShowBulkMoveModal] = useState(false)
    const [showBulkIssueModal, setShowBulkIssueModal] = useState(false)
    const [showBulkDisposeModal, setShowBulkDisposeModal] = useState(false)
    
    // Warehouse validation states
    const [commonWarehouse, setCommonWarehouse] = useState(null)
    const [validationError, setValidationError] = useState('')

    // Check if bulk actions are available based on warehouse compatibility
    const isBulkActionsAvailable = () => {
        // Need at least 1 selected item
        if (selectedItems.length === 0) {
            return false
        }

        // Block if any selected item is allocated to an active production LOT
        const hasInProductionItem = selectedItems.some(item => {
            const inventoryItem = inventoryItems.find(invItem => invItem._id === item.id)
            return inventoryItem?.in_production
        })
        if (hasInProductionItem) return false

        // If only one item selected, always available
        if (selectedItems.length === 1) {
            return true
        }

        let warehouseSet = new Set()
        
        // Loop through selected items and check their warehouses
        for (const selectedItem of selectedItems) {
            let warehouseName = null
            
            // Check warehouse information from selectedItem
            if (selectedItem.warehouse_name) {
                warehouseName = selectedItem.warehouse_name
            } else if (selectedItem.warehouse) {
                warehouseName = selectedItem.warehouse
            } else {
                // Find the corresponding inventory item to get the warehouse
                const inventoryItem = inventoryItems.find(item => item._id === selectedItem.id)
                if (inventoryItem) {
                    warehouseName = inventoryItem.location?.warehouse?.name || inventoryItem.warehouse_name
                }
            }
            
            if (warehouseName) {
                warehouseSet.add(warehouseName)
            }
        }

        // All items must be from the same warehouse for bulk operations
        return warehouseSet.size <= 1
    }

    // Validate warehouse compatibility for bulk operations
    const validateWarehouseForBulkOperation = (operationType) => {
        if (selectedItems.length === 0) {
            return { success: false }
        }

        let detectedWarehouse = null
        let detectedWarehouseId = null
        
        // First, try to use stored warehouse info directly from selected items
        for (const selectedItem of selectedItems) {
            let currentWarehouseName = null
            let currentWarehouseId = null
            
            // Use stored warehouse information if available
            if (selectedItem.warehouse_name) {
                currentWarehouseName = selectedItem.warehouse_name
                currentWarehouseId = selectedItem.warehouse_id
            } else if (selectedItem.warehouse) {
                currentWarehouseName = selectedItem.warehouse
                currentWarehouseId = selectedItem.warehouse_id
            } else {
                // Fallback to finding warehouse from inventory items
                const inventoryItem = inventoryItems.find(item => item._id === selectedItem.id)
                if (inventoryItem) {
                    currentWarehouseName = inventoryItem.location?.warehouse?.name || inventoryItem.warehouse_name
                    currentWarehouseId = inventoryItem.location?.warehouse?._id || inventoryItem.warehouse_id
                }
            }
            
            if (currentWarehouseName) {
                if (!detectedWarehouse) {
                    detectedWarehouse = currentWarehouseName
                    detectedWarehouseId = currentWarehouseId
                    
                    // If we don't have warehouse ID but have warehouse name, try to find it in warehouseMap
                    if (!detectedWarehouseId && warehouseMap) {
                        detectedWarehouseId = Object.keys(warehouseMap).find(
                            id => warehouseMap[id] === currentWarehouseName
                        )
                    }
                } else if (detectedWarehouse !== currentWarehouseName) {
                    return { 
                        success: false, 
                        error: `Cannot ${operationType} items from different warehouses: ${detectedWarehouse} and ${currentWarehouseName}` 
                    }
                }
            } else {
                // If no warehouse information found for any item
                console.warn('No warehouse information found for selected item:', selectedItem)
            }
        }

        if (operationType !== 'dispose') {
            // Check if any selected items are in transit
            const inTransitItems = selectedItems.filter(item => {
                const inventoryItem = inventoryItems.find(invItem => invItem._id === item.id)
                return inventoryItem && inventoryItem.is_in_transit
            })

            if (inTransitItems.length > 0) {
                return {
                    success: false,
                    error: `Cannot ${operationType} items that are in transit`
                }
            }
        }

        // Check if any selected items are allocated to an active production LOT
        const inProductionItems = selectedItems.filter(item => {
            const inventoryItem = inventoryItems.find(invItem => invItem._id === item.id)
            return inventoryItem?.in_production
        })

        if (inProductionItems.length > 0) {
            return {
                success: false,
                error: `Cannot ${operationType} items that are allocated to an active production LOT`
            }
        }

        // If no warehouse detected, return error
        if (!detectedWarehouse) {
            return { 
                success: false, 
                error: `No warehouse information available. Please ensure items have warehouse data.` 
            }
        }
        
        // Create warehouse object for compatibility with modals
        const warehouseInfo = {
            name: detectedWarehouse,
            id: detectedWarehouseId
        }
        
        return { success: true, warehouse: warehouseInfo }
    }

    // Update common warehouse when selected items change
    useEffect(() => {
        const validation = validateWarehouseForBulkOperation('move')
        setCommonWarehouse(validation.warehouse || null)
        setValidationError(validation.success ? '' : validation.error)
    }, [selectedItems, inventoryItems, warehouseMap])

    // Success handlers for bulk operations
    const handleSuccessfulBulkOperation = (refreshWarehouse = true) => {
        onSelectionClear?.()
        if (refreshWarehouse && onRefreshData) {
            onRefreshData()
        }
    }

    const handleSuccessfulBulkMove = () => handleSuccessfulBulkOperation(true)
    const handleSuccessfulBulkIssue = () => handleSuccessfulBulkOperation(true)
    const handleSuccessfulBulkDispose = () => handleSuccessfulBulkOperation(true)

    // Don't render if no items selected
    if (selectedItems.length === 0) {
        return null
    }

    return (
        <div className={`d-flex align-items-center gap-2 ${className}`}>
            <span className="text-body-secondary small me-2">
                <strong>{selectedItems.length}</strong> item{selectedItems.length !== 1 ? 's' : ''} selected
            </span>
            {isBulkActionsAvailable() && (
                <ButtonGroup>
                    {hasPermission('inventory.move') && (
                        <OverlayTrigger
                            overlay={<Tooltip>Move selected items to a different location</Tooltip>}
                            placement="top"
                        >
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                    const moveValidation = validateWarehouseForBulkOperation('move')
                                    if (moveValidation.success) {
                                        setCommonWarehouse(moveValidation.warehouse)
                                        setShowBulkMoveModal(true)
                                    } else {
                                        setValidationError(moveValidation.error)
                                    }
                                }}
                            >
                                <i className="material-icons" style={{fontSize: '14px', marginRight: '4px'}}>drive_file_move</i>
                                Move
                            </Button>
                        </OverlayTrigger>
                    )}

                    {hasPermission('inventory.issue') && (
                        <OverlayTrigger
                            overlay={<Tooltip>Issue selected items</Tooltip>}
                            placement="top"
                        >
                            <Button
                                variant="warning"
                                size="sm"
                                onClick={() => {
                                    const issueValidation = validateWarehouseForBulkOperation('issue')
                                    if (issueValidation.success) {
                                        setCommonWarehouse(issueValidation.warehouse)
                                        setShowBulkIssueModal(true)
                                    } else {
                                        setValidationError(issueValidation.error)
                                    }
                                }}
                            >
                                <i className="material-icons" style={{fontSize: '14px', marginRight: '4px'}}>output</i>
                                Issue
                            </Button>
                        </OverlayTrigger>
                    )}

                    {hasPermission('inventory.dispose') && (
                        <OverlayTrigger
                            overlay={<Tooltip>Dispose selected items</Tooltip>}
                            placement="top"
                        >
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                    const disposeValidation = validateWarehouseForBulkOperation('dispose')
                                    if (disposeValidation.success) {
                                        setCommonWarehouse(disposeValidation.warehouse)
                                        setShowBulkDisposeModal(true)
                                    } else {
                                        setValidationError(disposeValidation.error)
                                    }
                                }}
                            >
                                <i className="material-icons" style={{fontSize: '14px', marginRight: '4px'}}>delete</i>
                                Dispose
                            </Button>
                        </OverlayTrigger>
                    )}
                </ButtonGroup>
            )}
            
            <Button 
                variant="outline-danger" 
                size="sm"
                onClick={() => onSelectionClear?.()}
                className="ms-2"
            >
                Clear Selection
            </Button>
            
            {!isBulkActionsAvailable() && validationError && (
                <span className="text-body-secondary small">
                    ({validationError})
                </span>
            )}


            {/* Bulk Operation Modals */}
            <BulkMoveModal
                show={showBulkMoveModal}
                onHide={() => {
                    setShowBulkMoveModal(false)
                    setCommonWarehouse(null)
                }}
                selectedItems={selectedItems}
                warehouse={commonWarehouse}
                onSuccess={handleSuccessfulBulkMove}
            />

            <BulkIssueModal
                show={showBulkIssueModal}
                onHide={() => {
                    setShowBulkIssueModal(false)
                    setCommonWarehouse(null)
                }}
                selectedItems={selectedItems}
                warehouse={commonWarehouse}
                onSuccess={handleSuccessfulBulkIssue}
            />

            <BulkDisposeModal
                show={showBulkDisposeModal}
                onHide={() => {
                    setShowBulkDisposeModal(false)
                }}
                selectedItems={selectedItems}
                onSuccess={handleSuccessfulBulkDispose}
            />
        </div>
    )
}

export default BulkOperationsToolbar